import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TOPIC_PICKER } from '../../graphql';
import { Link, Video, Image, Upload, Search } from 'lucide-react';
import { TopicCard } from './TopicCard';
import { Topic, MediaType, Account } from '../../types';
import { GetTopicPickerResponse } from '../../types/graphql';
import { CreateTopicModal } from './CreateTopicModal';
import { TopicDetailsModal } from './TopicDetailsModal';
import { Loader } from '../common/PageLoader';

const PAGE_SIZE = 40;

interface TopicsSectionProps {
  accountId?: string;
  onTopicSelect: (topic: Topic) => void;
  mediaFilters: MediaType[];
  onMediaFilterChange: (event: any, newFilters: MediaType[]) => void;
  selectedAccount?: Pick<Account, 'id'> | null;
  selectedPost?: any;
  onRefetchReady?: (refetch: () => Promise<any>) => void;
  clickBehavior?: 'select' | 'details'; // 'select' calls onTopicSelect, 'details' shows details modal
}

export const TopicsSection = React.memo<TopicsSectionProps>(
  ({
    accountId,
    onTopicSelect,
    mediaFilters,
    onMediaFilterChange,
    selectedAccount,
    onRefetchReady,
    clickBehavior = 'select',
  }) => {
    const [isTopicModalOpen, setIsTopicModalOpen] = useState<boolean>(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
    const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    // Search waits for a pause in typing, then asks the server.
    const [query, setQuery] = useState<string>('');
    useEffect(() => {
      const t = window.setTimeout(() => setQuery(searchQuery.trim()), 300);
      return () => window.clearTimeout(t);
    }, [searchQuery]);

    // Determine active filter for UI display
    // Check if all media types are selected (ALL)
    const allMediaTypes = ['IMAGE', 'VIDEO', 'GALLERY', 'PHOTO', 'BOOKMARK'];
    const isAllSelected = allMediaTypes.every((type) => mediaFilters.includes(type as MediaType));
    // Check if Bookmarks filter is active
    // Case 1: Both IMAGE and BOOKMARK selected (and only those two)
    // Case 2: Only BOOKMARK selected
    const isBookmarksFilter =
      (mediaFilters.length === 2 &&
        mediaFilters.includes('IMAGE' as MediaType) &&
        mediaFilters.includes('BOOKMARK' as MediaType)) ||
      (mediaFilters.length === 1 && mediaFilters.includes('BOOKMARK' as MediaType));
    // Check if Photos filter is active (IMAGE, PHOTO, and/or GALLERY selected, and no other types)
    const photoTypes = ['IMAGE', 'PHOTO', 'GALLERY'];
    const isPhotosFilter =
      mediaFilters.length > 0 &&
      mediaFilters.every((filter) => photoTypes.includes(filter as string)) &&
      !mediaFilters.includes('VIDEO' as MediaType) &&
      !mediaFilters.includes('BOOKMARK' as MediaType);
    const activeFilter = isAllSelected
      ? 'ALL'
      : isBookmarksFilter
        ? 'BOOKMARK'
        : isPhotosFilter
          ? 'PHOTO'
          : mediaFilters[0] || 'ALL';
    // The media types the server filters by: none for All, bookmarks with images.
    const mediaTypes: string[] | null =
      activeFilter === 'ALL'
        ? null
        : isBookmarksFilter
          ? ['BOOKMARK', 'IMAGE']
          : [...mediaFilters].sort();

    // A page at a time, filtered, searched and in order (newest first) by the
    // server; "Load more" appends the next page.
    const {
      loading,
      error,
      data: topicsData,
      refetch,
      fetchMore,
    } = useQuery<GetTopicPickerResponse>(GET_TOPIC_PICKER, {
      variables: {
        accountId: selectedAccount?.id || '',
        mediaTypes,
        search: query || null,
        offset: 0,
        limit: PAGE_SIZE,
      },
      skip: !selectedAccount?.id,
      fetchPolicy: 'cache-and-network',
    });
    const topics = topicsData?.topicPicker.items || [];
    const total = topicsData?.topicPicker.total ?? 0;
    const [loadingMore, setLoadingMore] = useState(false);
    const loadMore = async () => {
      setLoadingMore(true);
      try {
        await fetchMore({
          variables: { offset: topics.length },
          updateQuery: (prev, { fetchMoreResult }) =>
            fetchMoreResult
              ? {
                  topicPicker: {
                    ...fetchMoreResult.topicPicker,
                    items: [
                      ...(prev?.topicPicker.items || []),
                      ...fetchMoreResult.topicPicker.items,
                    ],
                  },
                }
              : prev,
        });
      } finally {
        setLoadingMore(false);
      }
    };

    // Expose refetch function to parent - only if accountId exists
    useEffect(() => {
      if (!onRefetchReady) return;

      // Only set refetch function if we have a valid accountId and refetch is available
      if (selectedAccount?.id && refetch) {
        onRefetchReady(() => {
          // Double-check accountId still exists before refetching
          if (selectedAccount?.id) {
            return refetch();
          }
          return Promise.resolve();
        });
      } else {
        // Set a no-op function if accountId is missing or refetch is not available
        onRefetchReady(async () => {
          // No-op: no accountId or query is skipped, nothing to refetch
          return Promise.resolve();
        });
      }
    }, [onRefetchReady, refetch, selectedAccount?.id]);

    const handleTopicModalClose = () => {
      setIsTopicModalOpen(false);
    };

    const handleTopicCreated = () => {
      refetch();
    };

    const handleTopicClick = (topic: Topic) => {
      if (clickBehavior === 'details') {
        setSelectedTopic(topic);
        setIsDetailsModalOpen(true);
      } else {
        onTopicSelect(topic);
      }
    };

    const handleCloseDetailsModal = () => {
      setIsDetailsModalOpen(false);
      setSelectedTopic(null);
    };

    return (
      <div className="bg-core-dark p-6 h-full flex flex-col">
        <div className="mb-4 flex-shrink-0">
          <h2 className="text-xl font-semibold text-cmp-ink">Media Library</h2>
        </div>

        {/* Add Topic Modal */}
        {accountId && (
          <CreateTopicModal
            isOpen={isTopicModalOpen}
            onClose={handleTopicModalClose}
            accountId={accountId}
            onSuccess={handleTopicCreated}
          />
        )}

        {/* Topic Details Modal */}
        <TopicDetailsModal
          topic={selectedTopic}
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
        />

        <div className="mb-4 flex-shrink-0 space-y-3">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cmp-ink-2" />
              <input
                type="text"
                placeholder="Search by title, summary, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-1 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-500 text-sm"
              />
            </div>
            <button
              onClick={() => setIsTopicModalOpen(true)}
              className="px-3 py-1 rounded bg-primary text-onfill hover:bg-primary-dark transition-colors border border-primary flex-shrink-0 flex items-center gap-2"
              title="Upload new content"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm">Upload</span>
            </button>
          </div>
          <div className="flex w-full border border-cmp-line-2 rounded overflow-hidden bg-form-dark p-0.5">
            <button
              onClick={() => {
                onMediaFilterChange(null, ['IMAGE', 'VIDEO', 'GALLERY', 'PHOTO', 'BOOKMARK']);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                activeFilter === 'ALL'
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <span>All</span>
            </button>
            <button
              onClick={() => {
                onMediaFilterChange(null, ['IMAGE', 'BOOKMARK']);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                isBookmarksFilter
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <Link className="w-3 h-3" />
              <span>Bookmarks</span>
            </button>
            <button
              onClick={() => {
                onMediaFilterChange(null, ['VIDEO']);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                activeFilter === 'VIDEO'
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <Video className="w-3 h-3" />
              <span>Videos</span>
            </button>
            <button
              onClick={() => {
                onMediaFilterChange(null, ['PHOTO' as MediaType]);
              }}
              className={`flex-1 px-2 py-0.5 flex items-center justify-center gap-1 text-xs transition-colors rounded ${
                activeFilter === 'PHOTO'
                  ? 'bg-primary text-onfill'
                  : 'bg-transparent text-cmp-ink-2 hover:text-cmp-ink'
              }`}
            >
              <Image className="w-3 h-3" />
              <span>Photos</span>
            </button>
          </div>
        </div>

        {loading && !topicsData ? (
          <div className="flex justify-center">
            <Loader label="Loading topics" className="text-cmp-ink" />
          </div>
        ) : error ? (
          <div className="p-4 bg-error/20 border border-error rounded text-error">
            Error loading topics: {error.message}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto -mx-6 px-6">
            {topics.length === 0 ? (
              <p className="text-sm text-cmp-ink-2 py-4">
                {query || mediaTypes
                  ? 'Nothing matches. Try a different filter or search.'
                  : 'Nothing here yet. Upload something to get started.'}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4 pb-4">
                {topics.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} onClick={() => handleTopicClick(topic)} />
                ))}
              </div>
            )}
            {topics.length > 0 && (
              <div className="flex items-center justify-between gap-3 pb-4 text-xs text-cmp-ink-2">
                <span>
                  Showing {topics.length.toLocaleString()} of {total.toLocaleString()}
                </span>
                {topics.length < total && (
                  <button
                    type="button"
                    onClick={() => void loadMore()}
                    disabled={loadingMore}
                    className="px-3 py-1 rounded border border-cmp-line-2 text-cmp-ink hover:border-primary transition-colors disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
